import type {
    CheckoutSessionService,
    CheckoutSessionParams,
    CheckoutSessionResult
} from '../../../infrastructure/services/ApiPaymentService';
import type {IProductRepository} from '../../../domain/repositories/IProductRepository';
import type {IUserRepository} from '../../../domain/repositories/IUserRepository';

export interface CreateCheckoutSessionParams {
    productId: string;
    quantity: number;
    paymentMethod: string;
    paymentPlan?: {
        durationMonths: number;
        frequency: string;
        kmpEnabled: boolean;
    };
    deliveryOption?: string;
    deliveryAddressId?: string;
    userId: string;
}

export class CreateCheckoutSessionUseCase {
    constructor(
        private readonly checkoutSessionService: CheckoutSessionService,
        private readonly productRepository: IProductRepository,
        private readonly userRepository: IUserRepository
    ) {
    }

    async execute(params: CreateCheckoutSessionParams): Promise<CheckoutSessionResult> {
        // 1. Validate product exists and is available
        const product = await this.productRepository.findById(params.productId);
        if (!product) {
            throw new Error('Product not found');
        }

        if (!product.isAvailable()) {
            throw new Error('Product is not available');
        }

        // 2. Validate user exists
        const user = await this.userRepository.findById(params.userId);
        if (!user) {
            throw new Error('User not found');
        }

        // 3. Validate payment plan if PSS is selected
        if (params.paymentMethod === 'PSS' && params.paymentPlan) {
            await this.validatePSSEligibility(user, product, params.paymentPlan);
        }

        // 4. Create checkout session
        const paymentOption =
          String(params.paymentMethod).toUpperCase() === 'PSS' ? 'PSS' : 'PAY_IN_FULL';
        const sessionParams: CheckoutSessionParams = {
          productId: params.productId,
          quantity: params.quantity,
          paymentMethod: params.paymentMethod,
          paymentPlan: params.paymentPlan,
          deliveryOption: params.deliveryOption,
          deliveryAddressId: params.deliveryAddressId,
          paymentOption
        };

        const session = await this.checkoutSessionService.createSession(sessionParams);

        return session;
    }

    private async validatePSSEligibility(user: any, product: any, paymentPlan: any): Promise<void> {
        // Check if user is eligible for credit
        if (!user.isEligibleForCredit()) {
            throw new Error('User is not eligible for PSS credit');
        }

        // Check if product is eligible for payment plans
        if (!product.isEligibleForPaymentPlan()) {
            throw new Error('Product is not eligible for payment plans');
        }

        // Validate plan duration is within product limits
        if (paymentPlan.durationMonths > product.maxPaymentPeriod) {
            throw new Error(`Payment plan duration exceeds maximum allowed (${product.maxPaymentPeriod} months)`);
        }

        // Check if user can afford the plan
        const estimatedMonthlyAmount = product.retailPrice / paymentPlan.durationMonths;
        if (estimatedMonthlyAmount > user.spendingLimit) {
            throw new Error('Monthly payment amount exceeds user spending limit');
        }
    }
}
